<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import {
  CheckCircle2,
  KeyRound,
  RefreshCw,
  ShieldCheck,
} from "lucide-vue-next";
import ExternalTotpEnrollment from "@/components/auth/ExternalTotpEnrollment.vue";
import {
  externalTotpFactors,
  finishExternalTotpEnrollment,
  startExternalTotpEnrollment,
  type ExternalMfaFactor,
  type ExternalTotpEnrollment as ExternalTotpEnrollmentState,
} from "@/services/firebase";
import { externalMfaErrorMessage } from "@/utils/auth-errors";

type Mode = "idle" | "password" | "setup";

const factors = ref<ExternalMfaFactor[]>([]);
const mode = ref<Mode>("idle");
const password = ref("");
const enrollment = shallowRef<ExternalTotpEnrollmentState | null>(null);
const busy = ref(false);
const error = ref("");
const success = ref("");

function refreshFactors() {
  factors.value = externalTotpFactors();
}

function resetFlow() {
  mode.value = "idle";
  password.value = "";
  enrollment.value = null;
  error.value = "";
}

async function prepareEnrollment() {
  if (!password.value || busy.value) return;
  busy.value = true;
  error.value = "";
  success.value = "";
  try {
    enrollment.value = await startExternalTotpEnrollment(password.value);
    password.value = "";
    mode.value = "setup";
  } catch (enrollmentError) {
    error.value = externalMfaErrorMessage(enrollmentError, "enroll");
  } finally {
    busy.value = false;
  }
}

async function completeEnrollment(code: string) {
  if (!enrollment.value) return;
  busy.value = true;
  error.value = "";
  success.value = "";
  try {
    await finishExternalTotpEnrollment(enrollment.value, code);
    resetFlow();
    refreshFactors();
    success.value =
      "Authenticator app enabled. You’ll use a verification code the next time you sign in.";
  } catch (enrollmentError) {
    error.value = externalMfaErrorMessage(enrollmentError, "enroll");
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  try {
    refreshFactors();
  } catch (loadError) {
    error.value = externalMfaErrorMessage(loadError, "enroll");
  }
});

onBeforeUnmount(() => {
  enrollment.value = null;
  password.value = "";
});
</script>

<template>
  <section class="mfa-section sheet" aria-labelledby="mfa-settings-title">
    <header>
      <span class="section-icon"><ShieldCheck :size="20" /></span>
      <div>
        <h2 id="mfa-settings-title">Authenticator app</h2>
        <p>Add a verification code when you sign in with your password.</p>
      </div>
      <span
        class="mfa-status"
        :class="{ 'mfa-status--enabled': factors.length }"
      >
        {{ factors.length ? "Enabled" : "Required" }}
      </span>
    </header>

    <div class="mfa-body">
      <p v-if="success" class="mfa-success" role="status">
        <CheckCircle2 :size="17" aria-hidden="true" />
        {{ success }}
      </p>

      <template v-if="mode === 'idle' && !factors.length">
        <div class="mfa-summary">
          <div>
            <h3>Set up your required sign-in verification</h3>
            <p>
              Use an authenticator app such as Microsoft Authenticator, Google
              Authenticator, or 1Password. External accounts must complete this
              step before accessing project content.
            </p>
          </div>
          <button
            class="button button--primary"
            type="button"
            @click="
              mode = 'password';
              error = '';
              success = '';
            "
          >
            <KeyRound :size="16" aria-hidden="true" />
            Add authenticator app
          </button>
        </div>
      </template>

      <form
        v-else-if="mode === 'password'"
        class="mfa-form"
        @submit.prevent="prepareEnrollment"
      >
        <div>
          <h3>Confirm your password</h3>
          <p>Enter your current password before changing sign-in security.</p>
        </div>
        <div class="field">
          <label for="mfa-current-password">Current password</label>
          <input
            id="mfa-current-password"
            v-model="password"
            class="input"
            type="password"
            autocomplete="current-password"
            required
            autofocus
          />
        </div>
        <div class="mfa-actions">
          <button
            class="button button--secondary"
            type="button"
            :disabled="busy"
            @click="resetFlow"
          >
            Cancel
          </button>
          <button
            class="button button--primary"
            type="submit"
            :disabled="busy || !password"
          >
            <RefreshCw v-if="busy" class="spin" :size="16" />
            {{ busy ? "Checking…" : "Continue" }}
          </button>
        </div>
      </form>

      <ExternalTotpEnrollment
        v-else-if="mode === 'setup' && enrollment"
        :enrollment="enrollment"
        :busy="busy"
        :error="error"
        action-label="Enable authenticator app"
        @verify="completeEnrollment"
        @cancel="resetFlow"
      />

      <template v-else-if="factors.length && mode === 'idle'">
        <div class="mfa-summary">
          <div>
            <h3>{{ factors[0]?.displayName }}</h3>
            <p>
              Your password and a current verification code are required when
              you sign in.
            </p>
          </div>
        </div>
        <p class="recovery-note">
          If you lose access to your authenticator app, ask your workspace
          administrator to reset it after verifying your identity.
        </p>
      </template>

      <p v-if="error && mode !== 'setup'" class="mfa-error" role="alert">
        {{ error }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.mfa-section {
  overflow: hidden;
}
.mfa-section > header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid var(--rule);
}
.mfa-section > header > div {
  min-width: 0;
  flex: 1;
}
.mfa-section header p,
.mfa-summary p,
.mfa-form > div:first-child p {
  margin-top: 5px;
  color: var(--text-soft);
}
.section-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.mfa-status {
  min-height: 25px;
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  color: var(--text-soft);
  background: var(--sheet-strong);
  font-size: 12px;
  font-weight: 700;
}
.mfa-status--enabled {
  color: var(--success);
  background: var(--success-soft);
}
.mfa-body {
  display: grid;
  gap: 16px;
  padding: 20px 22px;
}
.mfa-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.mfa-summary > div {
  min-width: 0;
  max-width: 68ch;
}
.mfa-form {
  display: grid;
  gap: 16px;
  max-width: 680px;
}
.mfa-form .input {
  min-height: 44px;
}
.mfa-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}
.recovery-note,
.mfa-error,
.mfa-success {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 12px;
  border-radius: var(--radius-sm);
}
.recovery-note {
  color: var(--text-soft);
  background: var(--sheet-strong);
  font-size: 12px;
}
.mfa-error {
  color: var(--danger);
  background: var(--danger-soft);
}
.mfa-success {
  color: var(--success);
  background: var(--success-soft);
}
.mfa-success svg {
  flex: 0 0 auto;
}
.spin {
  animation: spin 900ms linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 620px) {
  .mfa-section > header,
  .mfa-body {
    padding-inline: 16px;
  }
  .mfa-summary {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }
  .mfa-summary .button,
  .mfa-actions .button {
    width: 100%;
    min-height: 44px;
  }
  .mfa-actions {
    flex-direction: column-reverse;
  }
}
@media (prefers-reduced-motion: reduce) {
  .spin {
    animation: none;
  }
}
</style>
